import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import config from '../../../config/config';
import handleOpenSweetAlert from '../../../components/handleOpenSweetAlert';
import { useSession } from '../../../components/hooks/useSession';

const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];
  if (Array.isArray(idEmpresa)) return idEmpresa.map(x => Number(x)).filter(n => Number.isFinite(n) && n > 0);
  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();
    if (s.startsWith('[') && s.endsWith(']')) {
      try { const arr = JSON.parse(s); if (Array.isArray(arr)) return arr.map(x => Number(x)).filter(n => Number.isFinite(n) && n > 0); } catch {}
    }
    const n = Number(s); return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  return Number.isFinite(Number(idEmpresa)) && Number(idEmpresa) > 0 ? [Number(idEmpresa)] : [];
};

const getMimeTypeFromExtension = (ext) => {
  switch (ext) {
    case 'pdf': return 'application/pdf';
    case 'jpg': case 'jpeg': case 'png': case 'gif': case 'webp': return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
    case 'xml': return 'application/xml';
    case 'xls': return 'application/vnd.ms-excel';
    case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'doc': return 'application/msword';
    case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'ppt': return 'application/vnd.ms-powerpoint';
    case 'pptx': return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    default: return 'application/octet-stream';
  }
};



export const useTesoreroSolicitudesController = () => {
  const apiUrl = config.apiBaseUrl;
  const { idPerfil, loggedInUsername, nombreEmpresa, idEmpresa } = useSession();

  const usuarioId = useMemo(() => Number(sessionStorage.getItem('UsuarioId') || 0), []);
  const empresaIds = useMemo(() => normalizeEmpresaArray(idEmpresa), [idEmpresa]);
  const empresaIdPref = useMemo(() => (empresaIds.length > 1 ? 0 : (empresaIds[0] ?? 0)), [empresaIds]);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openFileModal, setOpenFileModal] = useState(false);
  const [fileModalFiles, setFileModalFiles] = useState([]);
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  const baseFilters = useMemo(() => ({
    FECHA_INICIO: '',
    FECHA_FIN: '',
    FOLIO: '',
    EMPRESA: '',
  }), []);

  const [tempFilterData, setTempFilterData] = useState(baseFilters);
  const [filterData, setFilterData] = useState(baseFilters);

  const ejecutarBusquedaManual = useCallback((filtrosPasados = null) => {
    setFilterData(filtrosPasados || tempFilterData);
  }, [tempFilterData]);

  const abortRef = useRef(null);

  const fetchData = useCallback(async (filtros) => {
    if (!apiUrl || !empresaIds.length) return;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const res = await axios.post(`${apiUrl}/tesoreroSolicitudesTabla`, {
        loggedInUsername, idPerfil, nombreEmpresa, idEmpresa: empresaIds, filterData: filtros
      }, { signal: controller.signal });
      const rawRows = Array.isArray(res.data) ? res.data : (res.data?.contenido || []);
      const sortedRows = [...rawRows].sort((a, b) => Number(b.ID_SOLICITUD || b.ID || 0) - Number(a.ID_SOLICITUD || a.ID || 0));
      setData(res.data?.contenido ? { ...res.data, contenido: sortedRows } : sortedRows);
    } catch (err) {
      if (err?.name === 'CanceledError') return;
      handleOpenSweetAlert('Error al obtener datos', 'error');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [apiUrl, empresaIds, idPerfil, loggedInUsername, nombreEmpresa]);

  useEffect(() => {
    fetchData(filterData);
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchData, filterData]);

  const handleViewFile = useCallback(async (row) => {
    try {
      const response = await axios.post(`${apiUrl}/comprobanteAutDescargar`, {
        id: row.ID_SOLICITUD || row.ID,
        folioOc: row.FOLIO_OC || row.FOLIO,
        empresaId: row.EMPRESAID
      });
      const { adjuntos } = response.data;
      if (!adjuntos || !adjuntos.length) { handleOpenSweetAlert('No hay archivos adjuntos para esta solicitud.', 'info'); return; }
      
      const officeExts = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
      const files = adjuntos.map((file) => {
        const ext = file.FILENAME.split('.').pop().toLowerCase();
        const previewFileType = officeExts.includes(ext) ? 'application/pdf' : getMimeTypeFromExtension(ext);
        return {
          id: file.SOLICITUD_ID, filename: file.FILENAME, fileType: previewFileType,
          previewUrl: `${apiUrl}/visualizarComprobanteTesorero/${file.SOLICITUD_ID}/${encodeURIComponent(file.FILENAME)}`,
          downloadUrl: `${apiUrl}/descargarComprobanteTesorero/${file.SOLICITUD_ID}/${encodeURIComponent(file.FILENAME)}`,
        };
      });

      setFileModalFiles(files);
      setOpenFileModal(true);
    } catch (_error) {
      handleOpenSweetAlert('Error al obtener archivos adjuntos', 'error');
    }
  }, [apiUrl]);

  const handleUploadAdjuntos = async (row) => {
    if (!row?.FOLIO || !row?.ID_SOLICITUD || !row?.EMPRESA) { handleOpenSweetAlert('Datos incompletos para subir los archivos.', 'error'); return; }
    if (adjuntos.length === 0) { handleOpenSweetAlert('Debe seleccionar al menos un archivo.', 'warning'); return; }

    const formData = new FormData();
    formData.append('FOLIO', row.FOLIO);
    formData.append('ID_SOLICITUD', row.ID_SOLICITUD);
    formData.append('NOMBRE_EMPRESA', row.EMPRESA);
    adjuntos.forEach(file => formData.append('adjunto', file));

    try {
      const response = await axios.put(`${apiUrl}/tesoreroGuardarAdjuntos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      handleOpenSweetAlert(response.data.message, 'success');
      setFileUploadModalOpen(false);
      setAdjuntos([]);
      fetchData();
    } catch (error) {
      handleOpenSweetAlert(error.response?.data?.error || 'Error al subir archivos', 'error');
    }
  };

  const handleCustomZip = async (archivos) => {
    if (!selectedRow?.ID_SOLICITUD || !selectedRow?.FOLIO) { handleOpenSweetAlert('No hay información de la solicitud seleccionada.', 'error'); return; }
    const rutas = archivos.map(f => f.downloadUrl.replace(`${apiUrl}/<uploads>/`, ''));

    try {
      const response = await axios.post(`${apiUrl}/descargarZipPorRutas`, {
        rutas, ID_SOLICITUD: selectedRow.ID_SOLICITUD, FOLIO: selectedRow.FOLIO,
      }, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedRow.ID_SOLICITUD}_${selectedRow.FOLIO}_Comprobante_Pago.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_error) {
      handleOpenSweetAlert('Error al generar el archivo ZIP', 'error');
    }
  };

  return {
    data, loading, setLoading,
    openFileModal, setOpenFileModal, fileModalFiles,
    fileUploadModalOpen, setFileUploadModalOpen,
    adjuntos, setAdjuntos, selectedRow, setSelectedRow,
    empresaIds, empresaIdPref, usuarioId,
    tempFilterData, setTempFilterData, ejecutarBusquedaManual,
    handleViewFile, handleUploadAdjuntos, handleCustomZip, apiUrl
  };
};
